import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button, Input, Popover, PopoverContent, PopoverTrigger, cn } from '@duck/ui'
import { UserService } from '@duck/api-definitions'
import { useTar } from '@/modules/i18n/useTar'
import { DISCOVERY_SOURCE_GROUPS, findDiscoverySourceOption } from './discoverySourceOptions'

export type DiscoverySourceValue = { source: UserService.DiscoverySource; other?: string }

type Props = {
    value: DiscoverySourceValue | null
    onChange: (value: DiscoverySourceValue) => void
    disabled?: boolean
    invalid?: boolean
    id?: string
}

export function DiscoverySourcePicker({ value, onChange, disabled, invalid, id }: Props) {
    const { t } = useTar()
    const [open, setOpen] = useState(false)
    const [otherOpen, setOtherOpen] = useState(false)
    const [otherText, setOtherText] = useState('')

    const selected = value === null ? undefined : findDiscoverySourceOption(value.source)

    const select = (next: DiscoverySourceValue) => {
        onChange(next)
        setOpen(false)
        setOtherOpen(false)
    }

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            const isOther = value?.source === UserService.DiscoverySource.Other
            setOtherOpen(isOther)
            setOtherText(isOther ? value?.other ?? '' : '')
        }
        setOpen(nextOpen)
    }

    const confirmOther = () => {
        const text = otherText.trim()
        if (text === '') return
        select({ source: UserService.DiscoverySource.Other, other: text })
    }

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    id={id}
                    disabled={disabled}
                    className={cn(
                        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        invalid && 'border-error'
                    )}
                >
                    {selected === undefined ? (
                        <span className="text-muted-foreground">{t('personalInfo.discoverySourcePlaceholder')}</span>
                    ) : (
                        <span className="flex min-w-0 items-center gap-2">
                            <selected.Icon size={16} />
                            <span className="truncate">
                                {selected.value === UserService.DiscoverySource.Other && value?.other
                                    ? value.other
                                    : t(selected.labelKey)}
                            </span>
                        </span>
                    )}
                    <ChevronDown className="h-4 w-4 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[min(420px,95vw)] max-w-[95vw] p-3">
                <div className="grid grid-cols-3 gap-3">
                    {DISCOVERY_SOURCE_GROUPS.map((group) => (
                        <div key={group.labelKey}>
                            <div className="mb-2 text-xs font-medium uppercase text-muted-foreground">{t(group.labelKey)}</div>
                            <div className="flex flex-col gap-2">
                                {group.options.map(({ value: optionValue, labelKey, Icon }) => {
                                    const isOther = optionValue === UserService.DiscoverySource.Other
                                    const isSelected = value?.source === optionValue
                                    return (
                                        <button
                                            key={optionValue}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelected}
                                            onClick={() => (isOther ? setOtherOpen(!otherOpen) : select({ source: optionValue }))}
                                            className={cn(
                                                'flex flex-col items-center gap-1 rounded-md border border-border p-2 text-xs hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                                isSelected && 'border-primary bg-primary/10'
                                            )}
                                        >
                                            <Icon size={22} />
                                            {t(labelKey)}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                    {otherOpen && (
                        <div className="col-span-3 flex gap-2">
                            <Input
                                autoFocus
                                maxLength={100}
                                className="h-8 text-xs"
                                value={otherText}
                                placeholder={t('personalInfo.discoverySourceOtherPlaceholder')}
                                onChange={(e) => setOtherText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        confirmOther()
                                    }
                                }}
                            />
                            <Button type="button" size="sm" disabled={otherText.trim() === ''} onClick={confirmOther}>
                                {t('common.ok')}
                            </Button>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}
